import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, UserCog, ShieldAlert, AlertTriangle, RefreshCw, TrendingUp, Clock, Layers, Percent, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import EmployeeOrdersTab from './EmployeeOrdersTab';
import ManagerOrdersTab from './ManagerOrdersTab';

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Onglet groupé "Bons à crédit" :
 *  - Employés : plafond mensuel configurable (défaut 10 000 F)
 *  - Responsable Op. & Log  : plafond mensuel configurable (défaut 15 000 F)
 *
 * Les deux partagent la mécanique : remise 50%, double autorisation séquentielle (Responsable Op. & Log → D.G.),
 * stock déduit après les 2 autorisations, clôture mensuelle qui retient sur salaires.
 * 🆕 Fév 2026 : les plafonds sont lus depuis /api/admin/caisse-thresholds.
 */
const CreditOrdersGroupedTab = ({ currentUser, formatPrice, products }) => {
  const isAdmin = currentUser?.role === 'admin';
  const [empCap, setEmpCap] = useState(10000);
  const [mgrCap, setMgrCap] = useState(15000);
  const [showRetroModal, setShowRetroModal] = useState(false);
  const [retroLoading, setRetroLoading] = useState(false);
  const [retroPreview, setRetroPreview] = useState(null); // preview response
  const [retroMode, setRetroMode] = useState("over_cap"); // over_cap | pending | both_modes
  const [retroCustomReason, setRetroCustomReason] = useState("");

  // 🆕 Régularisation autorisations (bulk-authorize avec remise personnalisable)
  const [showRegModal, setShowRegModal] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regPreview, setRegPreview] = useState(null);
  const [regTarget, setRegTarget] = useState("pending"); // pending | authorized | both_targets
  const [regDiscount, setRegDiscount] = useState(0.5); // 0..1
  const [regCustomReason, setRegCustomReason] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/caisse-thresholds`);
        if (cancelled) return;
        const e = Number(r.data?.employee_monthly_cap);
        const m = Number(r.data?.manager_monthly_cap);
        if (Number.isFinite(e) && e >= 0) setEmpCap(e);
        if (Number.isFinite(m) && m >= 0) setMgrCap(m);
      } catch { /* silent fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fmt = typeof formatPrice === "function" ? formatPrice : (v) => Number(v || 0).toLocaleString("fr-FR");

  const openRetroPreview = async () => {
    setShowRetroModal(true);
    setRetroPreview(null);
    setRetroLoading(false);
    // le fetch se déclenche via useEffect quand mode/reason changent → 1er load ci-dessous
    await fetchRetroPreview(retroMode, retroCustomReason);
  };

  const fetchRetroPreview = async (mode, custom) => {
    setRetroLoading(true);
    try {
      const params = new URLSearchParams({
        apply: "false",
        scope: "both",
        mode: mode || "over_cap",
      });
      if (custom && custom.trim()) params.set("custom_reason", custom.trim());
      const r = await axios.post(`${API}/admin/personnel-orders/retro-cancel-over-cap?${params.toString()}`);
      setRetroPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'aperçu");
    } finally { setRetroLoading(false); }
  };

  // Recharge l'aperçu à chaque changement de mode / motif (débounce)
  useEffect(() => {
    if (!showRetroModal) return;
    const t = setTimeout(() => fetchRetroPreview(retroMode, retroCustomReason), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retroMode, retroCustomReason, showRetroModal]);

  // 🆕 Régularisation autorisations
  const openRegPreview = async () => {
    setShowRegModal(true);
    setRegPreview(null);
    await fetchRegPreview(regTarget, regDiscount, regCustomReason);
  };

  const fetchRegPreview = async (target, rate, custom) => {
    setRegLoading(true);
    try {
      const params = new URLSearchParams({
        apply: "false",
        scope: "both",
        target: target || "pending",
        discount_rate: String(rate ?? 0.5),
      });
      if (custom && custom.trim()) params.set("custom_reason", custom.trim());
      const r = await axios.post(`${API}/admin/personnel-orders/regularize-auth?${params.toString()}`);
      setRegPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'aperçu");
    } finally { setRegLoading(false); }
  };

  useEffect(() => {
    if (!showRegModal) return;
    const t = setTimeout(() => fetchRegPreview(regTarget, regDiscount, regCustomReason), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regTarget, regDiscount, regCustomReason, showRegModal]);

  const confirmRegApply = async () => {
    if (!regPreview || regPreview.total_affected === 0) return;
    setRegLoading(true);
    try {
      const params = new URLSearchParams({
        apply: "true",
        scope: "both",
        target: regTarget,
        discount_rate: String(regDiscount),
      });
      if (regCustomReason.trim()) params.set("custom_reason", regCustomReason.trim());
      const r = await axios.post(`${API}/admin/personnel-orders/regularize-auth?${params.toString()}`);
      toast.success(`${r.data.total_affected} bon(s) régularisé(s) — nouveau montant total ${fmt(r.data.total_new_amount)} F`);
      setShowRegModal(false);
      setRegPreview(null);
      setRegCustomReason("");
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'application");
    } finally { setRegLoading(false); }
  };

  const confirmRetroApply = async () => {
    if (!retroPreview || retroPreview.total_affected === 0) return;
    setRetroLoading(true);
    try {
      const params = new URLSearchParams({
        apply: "true",
        scope: "both",
        mode: retroMode,
      });
      if (retroCustomReason.trim()) params.set("custom_reason", retroCustomReason.trim());
      const r = await axios.post(`${API}/admin/personnel-orders/retro-cancel-over-cap?${params.toString()}`);
      toast.success(`Rétro-correction appliquée : ${r.data.total_affected} bon(s) marqué(s) NON AUTORISÉ (total dû ${fmt(r.data.total_owed)} F)`);
      setShowRetroModal(false);
      setRetroPreview(null);
      setRetroCustomReason("");
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'application");
    } finally { setRetroLoading(false); }
  };

  return (
    <div className="space-y-4">
      {/* 🆕 Barre d'actions admin — Rétro-correction plafond + Régularisation autorisations */}
      {isAdmin && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 justify-between px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <ShieldAlert className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
              <p className="text-amber-100 text-[12px] leading-snug">
                <span className="font-bold">Rétro-correction admin</span> : marque comme <span className="font-bold text-amber-200">NON AUTORISÉ</span> les bons en attente ou autorisés qui dépassent le plafond mensuel actuel, par employé et par mois.
              </p>
            </div>
            <Button
              onClick={openRetroPreview}
              className="bg-amber-600 hover:bg-amber-700 text-white h-9 text-[12.5px] font-semibold flex-shrink-0"
              data-testid="retro-cancel-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.4} /> Rétro-corriger le plafond
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-between px-3 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <CheckCircle className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
              <p className="text-emerald-100 text-[12px] leading-snug">
                <span className="font-bold">Régularisation des autorisations</span> : autorise en masse les bons en attente et applique une remise personnalisable (défaut <span className="font-bold text-emerald-200">50%</span>).
              </p>
            </div>
            <Button
              onClick={openRegPreview}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-[12.5px] font-semibold flex-shrink-0"
              data-testid="regularize-btn"
            >
              <Percent className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.4} /> Régulariser les autorisations
            </Button>
          </div>
        </div>
      )}

      <Tabs defaultValue="credit-employes" className="w-full" data-testid="credit-grouped-tab">
        <TabsList className="bg-slate-900/50 border border-slate-700/50 mb-4">
          <TabsTrigger value="credit-employes" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white" data-testid="credit-tab-employes">
            <Users className="w-4 h-4 mr-1.5" /> Employés <span className="ml-2 text-[10px] opacity-70">{fmt(empCap)} F/mois</span>
          </TabsTrigger>
          <TabsTrigger value="credit-gerante" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white" data-testid="credit-tab-gerante">
            <UserCog className="w-4 h-4 mr-1.5" /> Resp. Op. <span className="ml-2 text-[10px] opacity-70">{fmt(mgrCap)} F/mois</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="credit-employes">
          <EmployeeOrdersTab key={`emp-${refreshKey}`} currentUser={currentUser} formatPrice={formatPrice} products={products} />
        </TabsContent>

        <TabsContent value="credit-gerante">
          <ManagerOrdersTab key={`mgr-${refreshKey}`} currentUser={currentUser} formatPrice={formatPrice} products={products} />
        </TabsContent>
      </Tabs>

      {/* Modal aperçu rétro-correction */}
      <Dialog open={showRetroModal} onOpenChange={setShowRetroModal}>
        <DialogContent
          className="bg-slate-900 border-amber-500/60 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          style={{ width: "min(calc(100vw - 1.5rem), 42rem)" }}
          data-testid="retro-cancel-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-amber-300 flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5" /> Rétro-correction du plafond
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            {/* 🆕 Sélecteur de mode */}
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs font-semibold">Que faut-il rétro-corriger ?</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { v: "over_cap", lbl: "Dépassement plafond", Icon: TrendingUp, sub: "Bons > plafond mensuel" },
                  { v: "pending", lbl: "Bons en attente", Icon: Clock, sub: "Non encore autorisés" },
                  { v: "both_modes", lbl: "Les deux", Icon: Layers, sub: "Dépassement + En attente" },
                ].map((opt) => {
                  const Icn = opt.Icon;
                  const active = retroMode === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setRetroMode(opt.v)}
                      className={`flex flex-col items-start gap-0.5 p-2 rounded-md border text-left transition ${
                        active
                          ? "bg-amber-500/15 border-amber-500/60 text-amber-200 ring-1 ring-amber-500/30"
                          : "bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200"
                      }`}
                      data-testid={`retro-mode-${opt.v}`}
                    >
                      <Icn className="w-3.5 h-3.5" />
                      <span className="text-[11.5px] font-bold">{opt.lbl}</span>
                      <span className="text-[9.5px] opacity-75">{opt.sub}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 🆕 Motif personnalisé */}
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs font-semibold">
                Motif personnalisé <span className="text-slate-500 font-normal">(facultatif — remplace le motif automatique)</span>
              </Label>
              <Textarea
                value={retroCustomReason}
                onChange={(e) => setRetroCustomReason(e.target.value)}
                placeholder="ex: Non autorisée par la Direction · Produit indisponible · Dépasse budget mensuel prévu"
                className="bg-slate-800 border-slate-700 text-white text-sm min-h-[60px]"
                maxLength={200}
                data-testid="retro-custom-reason"
              />
            </div>

            {retroLoading && !retroPreview ? (
              <p className="text-slate-400 text-sm text-center py-4">Analyse en cours…</p>
            ) : retroPreview ? (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg bg-slate-800 p-2.5 text-center">
                    <p className="text-slate-400 text-[10.5px] uppercase">Bons Employés</p>
                    <p className="text-pink-300 text-xl font-black">{retroPreview.employee?.affected?.length || 0}</p>
                    <p className="text-slate-500 text-[10px]">Plafond {fmt(retroPreview.employee?.cap || 0)} F</p>
                  </div>
                  <div className="rounded-lg bg-slate-800 p-2.5 text-center">
                    <p className="text-slate-400 text-[10.5px] uppercase">Bons Resp. Op.</p>
                    <p className="text-violet-300 text-xl font-black">{retroPreview.manager?.affected?.length || 0}</p>
                    <p className="text-slate-500 text-[10px]">Plafond {fmt(retroPreview.manager?.cap || 0)} F</p>
                  </div>
                  <div className="rounded-lg bg-red-500/15 border border-red-500/40 p-2.5 text-center">
                    <p className="text-red-200 text-[10.5px] uppercase font-bold">Total dû</p>
                    <p className="text-red-100 text-xl font-black tabular-nums">{fmt(retroPreview.total_owed || 0)} F</p>
                    <p className="text-red-300 text-[10px]">{retroPreview.total_affected} bon(s)</p>
                  </div>
                </div>

                {retroPreview.total_affected === 0 ? (
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-3 text-center">
                    <p className="text-emerald-200 font-semibold text-sm">✅ Aucun bon ne dépasse le plafond actuel.</p>
                    <p className="text-emerald-300/80 text-[11px] mt-0.5">Rien à rétro-corriger.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-slate-300 text-[12.5px]">
                      Ces bons seront marqués <span className="font-bold text-red-300">NON AUTORISÉ</span> avec le motif détaillé (dépassement plafond). Les employés devront rembourser <span className="font-bold text-red-200">le total complet des plats sans remise</span>.
                    </p>
                    <div className="max-h-[280px] overflow-y-auto space-y-1.5" data-testid="retro-affected-list">
                      {[...(retroPreview.manager?.affected || []).map(a => ({ ...a, kind: "Resp. Op." })),
                        ...(retroPreview.employee?.affected || []).map(a => ({ ...a, kind: "Employé" }))]
                        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                        .map((a) => (
                        <div key={a.id} className="flex items-center gap-2 p-2 rounded-md bg-red-950/30 border border-red-500/30 text-[11.5px]">
                          <div className="flex-1 min-w-0">
                            <p className="text-red-100 font-semibold truncate flex items-center gap-1.5 flex-wrap">
                              <span className="text-red-300 text-[10px] uppercase">{a.kind}</span>
                              {a.trigger === "over_cap" ? (
                                <span className="text-[9px] bg-orange-500/20 text-orange-300 border border-orange-500/40 px-1 py-px rounded">DÉPASSEMENT</span>
                              ) : (
                                <span className="text-[9px] bg-blue-500/20 text-blue-300 border border-blue-500/40 px-1 py-px rounded">EN ATTENTE</span>
                              )}
                              <span>{a.employee_name} · {a.month_period}</span>
                            </p>
                            <p className="text-slate-400 text-[10.5px]">
                              {(a.created_at || "").slice(0, 10)} · avant : <span className="text-slate-300">{a.previous_status}</span>
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-red-200 font-black tabular-nums">{fmt(a.subtotal)} F</p>
                            <p className="text-slate-500 text-[9.5px]">montant dû</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>

          <DialogFooter className="mt-3 gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRetroModal(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="retro-cancel-close"
            >
              Fermer
            </Button>
            {retroPreview && retroPreview.total_affected > 0 && (
              <Button
                onClick={confirmRetroApply}
                disabled={retroLoading}
                className="bg-red-600 hover:bg-red-700 text-white font-bold"
                data-testid="retro-cancel-confirm"
              >
                <AlertTriangle className="w-4 h-4 mr-1.5" />
                Confirmer — Marquer {retroPreview.total_affected} bon(s) NON AUTORISÉ
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Modal régularisation autorisations */}
      <Dialog open={showRegModal} onOpenChange={setShowRegModal}>
        <DialogContent
          className="bg-slate-900 border-emerald-500/60 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          style={{ width: "min(calc(100vw - 1.5rem), 42rem)" }}
          data-testid="regularize-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-emerald-300 flex items-center gap-2 text-lg">
              <Percent className="w-5 h-5" /> Régularisation des autorisations
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            {/* Sélecteur de cible */}
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs font-semibold">Bons à régulariser</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { v: "pending", lbl: "En attente", Icon: Clock, sub: "pending_manager + pending_director" },
                  { v: "authorized", lbl: "Autorisés", Icon: CheckCircle, sub: "Recalcul du prix" },
                  { v: "both_targets", lbl: "Les deux", Icon: Layers, sub: "Pending + Autorisés" },
                ].map((opt) => {
                  const Icn = opt.Icon;
                  const active = regTarget === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setRegTarget(opt.v)}
                      className={`flex flex-col items-start gap-0.5 p-2 rounded-md border text-left transition ${
                        active
                          ? "bg-emerald-500/15 border-emerald-500/60 text-emerald-200 ring-1 ring-emerald-500/30"
                          : "bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200"
                      }`}
                      data-testid={`reg-target-${opt.v}`}
                    >
                      <Icn className="w-3.5 h-3.5" />
                      <span className="text-[11.5px] font-bold">{opt.lbl}</span>
                      <span className="text-[9.5px] opacity-75">{opt.sub}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Slider taux de remise */}
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs font-semibold flex items-center justify-between">
                <span>Taux de remise appliqué</span>
                <span className="text-emerald-300 font-black text-[15px] tabular-nums">{Math.round(regDiscount * 100)}%</span>
              </Label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={Math.round(regDiscount * 100)}
                  onChange={(e) => setRegDiscount(Number(e.target.value) / 100)}
                  className="flex-1 accent-emerald-500"
                  data-testid="reg-discount-slider"
                />
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(regDiscount * 100)}
                  onChange={(e) => setRegDiscount(Math.max(0, Math.min(100, Number(e.target.value))) / 100)}
                  className="bg-slate-800 border-slate-700 text-white w-20 h-8 text-xs"
                  data-testid="reg-discount-input"
                />
                <span className="text-slate-400 text-xs">%</span>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {[0, 0.25, 0.5, 0.75, 1].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setRegDiscount(v)}
                    className={`px-2 py-0.5 rounded text-[10.5px] font-semibold transition ${
                      Math.abs(regDiscount - v) < 0.001
                        ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
                        : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-white"
                    }`}
                  >
                    {v * 100}%
                  </button>
                ))}
              </div>
            </div>

            {/* Motif */}
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs font-semibold">
                Motif d&apos;audit <span className="text-slate-500 font-normal">(facultatif)</span>
              </Label>
              <Textarea
                value={regCustomReason}
                onChange={(e) => setRegCustomReason(e.target.value)}
                placeholder="ex: Régularisation exceptionnelle décidée par la Direction · Correction erreur historique"
                className="bg-slate-800 border-slate-700 text-white text-sm min-h-[48px]"
                maxLength={200}
                data-testid="reg-custom-reason"
              />
            </div>

            {regLoading && !regPreview ? (
              <p className="text-slate-400 text-sm text-center py-4">Analyse en cours…</p>
            ) : regPreview ? (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg bg-slate-800 p-2.5 text-center">
                    <p className="text-slate-400 text-[10.5px] uppercase">Bons concernés</p>
                    <p className="text-emerald-300 text-xl font-black">{regPreview.total_affected}</p>
                  </div>
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2.5 text-center">
                    <p className="text-emerald-200 text-[10.5px] uppercase font-bold">Nouveau montant</p>
                    <p className="text-emerald-100 text-xl font-black tabular-nums">{fmt(regPreview.total_new_amount)} F</p>
                    <p className="text-emerald-300 text-[10px]">Total à retenir</p>
                  </div>
                  <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-2.5 text-center">
                    <p className="text-blue-200 text-[10.5px] uppercase font-bold">Économie totale</p>
                    <p className="text-blue-100 text-xl font-black tabular-nums">-{fmt(regPreview.total_savings)} F</p>
                  </div>
                </div>

                {regPreview.total_affected === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-2">Aucun bon à régulariser pour ce critère.</p>
                ) : (
                  <div className="max-h-[260px] overflow-y-auto space-y-1.5" data-testid="reg-affected-list">
                    {[...(regPreview.manager?.affected || []).map(a => ({ ...a, kind: "Resp. Op." })),
                      ...(regPreview.employee?.affected || []).map(a => ({ ...a, kind: "Employé" }))]
                      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                      .map((a) => (
                      <div key={a.id} className="flex items-center gap-2 p-2 rounded-md bg-emerald-950/20 border border-emerald-500/25 text-[11.5px]">
                        <div className="flex-1 min-w-0">
                          <p className="text-emerald-100 font-semibold truncate flex items-center gap-1.5 flex-wrap">
                            <span className="text-emerald-300 text-[10px] uppercase">{a.kind}</span>
                            <span className="text-[9px] bg-slate-700 text-slate-300 border border-slate-600 px-1 py-px rounded">{a.previous_status}</span>
                            <span>{a.employee_name}</span>
                          </p>
                          <p className="text-slate-400 text-[10.5px]">
                            {(a.created_at || "").slice(0, 10)} · Prix normal <span className="text-white">{fmt(a.subtotal)} F</span>
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-emerald-200 font-black tabular-nums">{fmt(a.new_total)} F</p>
                          <p className="text-slate-500 text-[9.5px]">
                            <span className="line-through opacity-60">{fmt(a.previous_total)}</span> nouveau
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </div>

          <DialogFooter className="mt-3 gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRegModal(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="reg-close"
            >
              Fermer
            </Button>
            {regPreview && regPreview.total_affected > 0 && (
              <Button
                onClick={confirmRegApply}
                disabled={regLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                data-testid="reg-confirm"
              >
                <CheckCircle className="w-4 h-4 mr-1.5" />
                Confirmer — Autoriser {regPreview.total_affected} bon(s) à {Math.round(regDiscount * 100)}%
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditOrdersGroupedTab;
